import UsersPage from 'components/users/UsersPage';
import { useParams, useHistory } from 'react-router-dom';
import {
  useGetOfficesQuery,
  useGetGroupsQuery,
  useDeleteOfficeMutation,
  useUpdateOfficeMutation,
  useUserSearchQuery,
} from 'utils/wemble-api';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useState } from 'react';
import { OfficeDto, UserDto } from 'utils/wemble-api.generated';

const OfficeShow = () => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { data: offices } = useGetOfficesQuery();
  const { data: groupsRes } = useGetGroupsQuery();
  const group = groupsRes?.groups?.find((g) => g._id === offices?.find((o) => o._id === id)?.group);
  const office = offices?.find((o) => o._id === id);
  const groupId = typeof office?.group === 'string' ? office.group : office?.group?._id;

  // Reuse the all-users cache from GroupsPage (which we navigate from)
  const { data: searchResult, isLoading } = useUserSearchQuery(
    { includeAdmins: true, includeUncompleted: true },
    { refetchOnMountOrArgChange: true, refetchOnReconnect: true },
  );
  // Filter to this specific office + group on the frontend
  const users = searchResult?.users?.filter((user) => {
    const userGroupId = typeof user.group === 'string' ? user.group : (user.group as any)?._id;
    const userOfficeId = typeof user.office === 'string' ? user.office : (user.office as any)?._id;
    const userSecondaryGroupId = typeof user.secondaryGroup === 'string' ? user.secondaryGroup : (user.secondaryGroup as any)?._id;
    const userSecondaryOfficeId = typeof user.secondaryOffice === 'string' ? user.secondaryOffice : (user.secondaryOffice as any)?._id;
    return (userGroupId === groupId && userOfficeId === id) || (userSecondaryGroupId === groupId && userSecondaryOfficeId === id);
  }) as UserDto[] | undefined;

  const [deleteOffice, { isLoading: deletingOffice }] = useDeleteOfficeMutation();
  const [updateOffice, { isLoading: updatingOffice }] = useUpdateOfficeMutation();

  const handleDelete = async () => {
    if (!office?._id) return;
    await deleteOffice({
      id: office._id,
    });
    history.replace(`/groups/${group?._id}`);
  };

  const [editOffice, setEditOffice] = useState<OfficeDto | undefined>();
  const [newName, setNewName] = useState('');
  const disabled = deletingOffice || updatingOffice;

  const toggleRenameModal = (office?: OfficeDto) => {
    if (office) {
      setEditOffice(office);
      setNewName(office.name ?? '');
    } else if (!disabled) {
      setEditOffice(undefined);
      setNewName('');
    }
  };

  const handleUpdateOfficeName = async () => {
    if (office?._id && newName.length > 0) {
      await updateOffice({
        id: office._id,
        officeUpdateParams: {
          name: newName,
        },
      });
      toggleRenameModal();
    }
  };

  return (
    <>
      <UsersPage
        users={users}
        hideFilters
        loading={isLoading && !users}
        handleBack={
          group?.name && group._id
            ? {
                text: group.name,
                path: `/groups/${group._id}`,
              }
            : undefined
        }
        office={id}
        group={group?._id}
        onAfterUserModified={undefined}
      >
        <Button color="danger" onClick={() => setShowConfirmModal(true)} disabled={deletingOffice}>
          Delete
        </Button>
        <Button color="primary" style={{ marginLeft: 8 }} onClick={() => toggleRenameModal(office)}>
          Rename
        </Button>

        <Modal isOpen={showConfirmModal} toggle={() => !disabled && setShowConfirmModal(false)}>
          <ModalHeader toggle={() => !disabled && setShowConfirmModal(false)}>Delete</ModalHeader>
          <ModalBody>
            <b>Warning!</b> <br />
            {users?.filter((user) => !user.companyAdmin).length ? (
              <>
                This office cannot be deleted yet. The following{' '}
                {users.filter((user) => !user.companyAdmin).length === 1
                  ? 'user is'
                  : `${users.filter((user) => !user.companyAdmin).length} users are`}{' '}
                still in it — move them to another office first:
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  {users
                    .filter((user) => !user.companyAdmin)
                    .map((user) => (
                      <li key={user._id}>{user.name || user.email}</li>
                    ))}
                </ul>
              </>
            ) : (
              <>
                Are you sure you want to delete {office?.name}?
                {!!users?.filter((user) => user.companyAdmin).length && (
                  <p style={{ marginTop: 8, marginBottom: 0 }}>
                    {users
                      .filter((user) => user.companyAdmin)
                      .map((user) => user.name || user.email)
                      .join(', ')}{' '}
                    {users.filter((user) => user.companyAdmin).length === 1
                      ? 'is a company admin'
                      : 'are company admins'}{' '}
                    assigned to this office. Their accounts will be kept and unassigned from the office.
                  </p>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowConfirmModal(false)} disabled={disabled}>
              Cancel
            </Button>{' '}
            <Button
              color="danger"
              onClick={handleDelete}
              disabled={disabled || !!users?.filter((user) => !user.companyAdmin).length}
            >
              Delete
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={editOffice !== undefined} toggle={() => toggleRenameModal()}>
          <ModalHeader toggle={() => toggleRenameModal()}>Rename</ModalHeader>
          <ModalBody>
            <table className={'table'}>
              <tbody>
                <tr>
                  <th scope="row" className={'control-label'}>
                    New name
                  </th>
                  <td>
                    <input
                      value={newName}
                      type="text"
                      name="name"
                      className={'form-control'}
                      onChange={(ev) => setNewName(ev.target.value)}
                      disabled={disabled}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => toggleRenameModal()} disabled={disabled}>
              Cancel
            </Button>{' '}
            <Button color="primary" onClick={handleUpdateOfficeName} disabled={disabled || newName.length === 0}>
              Rename
            </Button>
          </ModalFooter>
        </Modal>
      </UsersPage>
    </>
  );
};

export default OfficeShow;
